/* eslint-disable jsx-a11y/alt-text */
import './css/ExploreList.css';
import './css/ExplorePage.css';

import { observer } from 'mobx-react-lite';
import { Suspense, useMemo, useState, useEffect, useCallback } from 'react';
import { ExploreElementType, FilterData } from '../../spinnaxBase/data/explore';
import {
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  useIonViewDidEnter,
} from '@ionic/react';
import { close } from 'ionicons/icons';

import _ from 'lodash';
import { isAlive } from 'mobx-state-tree';
import { LoadingFallback } from '../../App';
import { useStores } from '../../models';
import { useExploreElementStore } from '../../models/ExploreElementStore';
import {
  ExploreElement,
  ExploreElementModel,
} from 'models/ExploreElementModel/ExploreElementModel';
import Toolbar from './components/Toolbar';
import FilterEditModal from '../../components/modals/FilterEditModal';
import Icon from '../../components/icons/Icon';
import { getAllMockData } from './data/mockData';
import CreateElementModal from '../../components/modals/CreateElementModal';
import EditElementModal from '../../components/modals/EditElementModal';
import EventModal from '../../components/modals/EventModal';
import RankingModal from '../../components/modals/RankingModal';
import PersonModal from '../../components/modals/PersonModal';
import ContestModal from '../../components/modals/ContestModal';
import AgentModal from 'components/modals/AgentModal';
import { useDebugStore } from '../../models/DebugStore/DebugStore';
import { useCloudStorage } from 'services/Database/CloudStorageService';
import { rankerService } from '../../services/freak_ranker';
import PeopleSection from './components/PeopleSection';
import ContestSection from './components/ContestSection';
import EventSection from './components/EventsSection';
import RankingsSection from './components/RankingsSection';
import AgentsSection from './components/AgentsSection';
import FavoritesSection from './components/FavoritesSection';
import { isMockElement } from '../../util/mockDataDetection';
import { start } from 'repl';
import { logger } from 'services/Logger';

const equalId = (a: ExploreElement, b: ExploreElement) => a.id === b.id;

const ExplorePage: React.FC = observer(() => {
  const rootStore = useStores();
  const _cloudStorage = useCloudStorage();
  const exploreElementStore = useExploreElementStore();
  const debugStore = useDebugStore();

  // State für das Laden der Firebase-Benutzer
  const [_isLoadingUsers, _setIsLoadingUsers] = useState(false);

  // Lade ExploreElements beim Component Mount
  useEffect(() => {
    exploreElementStore.loadElements();
  }, [exploreElementStore]);

  // Erweitere den loadRankerUsers mit Debug-Informationen
  const loadRankerUsers = useCallback(async () => {
    try {
      // Check if user is authenticated
      const currentUser = rootStore.currentUser;
      if (!currentUser) {
        return [];
      }
      // Versuche mit größerer Seitengröße
      const rankerUsers = await rankerService.getAllUsers(0, 1000);

      // ✅ DEBUG: Prüfe ob API-Response vollständig ist
      if (rankerUsers.length === 0) {
        return [];
      }

      // Convert ranker users to ExploreElement objects
      const exploreElements = rankerUsers
        .filter((user) => {
          // Filter out users with invalid display names
          if (
            !user.displayName ||
            user.displayName.trim() === '' ||
            user.displayName.trim() === 'Unbekannter Benutzer' ||
            user.displayName === null
          ) {
            return false;
          }

          // Filter out users with no score (fake users often have score 0)
          if (user.score === null || user.score === undefined) {
            return false;
          }

          // Filter out users with invalid birthdays like '.8.1990'
          if (
            user.birthday &&
            (user.birthday.startsWith('.') || user.birthday.includes('null'))
          ) {
            return false;
          }

          return true;
        })
        .map((user, index) => {
          const elementData = {
            id: `ranker_user_${user.displayName}_${index}_${Date.now()}`, // ✅ Bessere ID mit Name
            name: user.displayName || 'Unbekannter Benutzer',
            type: ExploreElementType.Person,
            startDate: new Date(),
            endDate: new Date(),
            favorite: false,

            // Optional fields with proper defaults
            country: user.nationality || '',
            gender: '', // Not available in ranker data
            age: user.birthday ? calculateAge(user.birthday) : 20, // Default to 20 if no birthday
            location: user.nationality || '',
            nationality: user.nationality || '',
            description: `Score: ${user.score || 0}`, // ✅ Zeige Score in Beschreibung

            // Coordinates (not available)
            latitude: null,
            longitude: null,

            // Ranking fields
            rankings: [],
            rankingCategory: '',
            rankingPeriod: '',
            maxParticipants: 0,
            isLive: false,
          };
          return elementData;
        });
      return exploreElements;
    } catch (error) {
      if (error instanceof Error) {
        logger.error('Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name,
        });
      }

      return [];
    }
  }, [rootStore]);

  // Hilfsfunktion für Altersberechnung
  const calculateAge = (birthday: string): number => {
    try {
      const birthDate = new Date(birthday);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      return age > 0 ? age : 20;
    } catch (error) {
      return 20;
    }
  };

  // Load Ranker users when component mounts
  useEffect(() => {
    const fetchRankerUsers = async () => {
      _setIsLoadingUsers(true);
      try {
        const exploreElements = await loadRankerUsers();
      } catch (error) {
        logger.error('Failed to load Ranker users:', error);
      } finally {
        _setIsLoadingUsers(false);
      }
    };

    fetchRankerUsers();
  }, [loadRankerUsers]);

  const [activeTab, setActiveTab] = useState(0);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // State für die "Load More" Funktionalität
  const [appliedFilters, setAppliedFilters] = useState<FilterData>({
    locations: [],
    nationalities: [],
    elementTypes: [],
    favorites: false,
    genders: [],
    ageRange: undefined, // No age filter by default - only applied when explicitly set by user
    searchQuery: '',
  });

  // State für die CreateModal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRankingModalOpen, setIsRankingModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isPersonModalOpen, setIsPersonModalOpen] = useState(false);
  const [isContestModalOpen, setIsContestModalOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState<ExploreElement | null>(
    null
  );
  const [originalRanking, setOriginalRanking] = useState<ExploreElement | null>(
    null
  );
  const [selectedScore, setSelectedScore] = useState<number>(0);

  // State für Edit-Modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [elementToEdit, setElementToEdit] = useState<ExploreElement | null>(
    null
  );

  // State für Force Update - um UI-Updates nach Store-Änderungen zu triggern
  const [forceUpdateCounter, setForceUpdateCounter] = useState(0);

  // State für AgentModal
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);

  // Force update function
  const _forceUpdate = () => setForceUpdateCounter((prev) => prev + 1);

  const openRankingModal = (element: ExploreElement) => {
    setSelectedElement(element);
    setOriginalRanking(null); // Clear any previous original ranking
    setIsRankingModalOpen(true);
  };

  const closeRankingModal = () => {
    setIsRankingModalOpen(false);
  };

  const openEventModal = (element: ExploreElement) => {
    setSelectedElement(element);
    setOriginalRanking(null); // Clear any previous original ranking
    setIsEventModalOpen(true);
  };

  const closeEventModal = () => {
    setIsEventModalOpen(false);
  };

  const openContestModal = (element: ExploreElement) => {
    setSelectedElement(element);
    setOriginalRanking(null); // Clear any previous original ranking
    setIsContestModalOpen(true);
  };

  const closeContestModal = () => {
    setIsContestModalOpen(false);
  };

  // Person Click Handler für Contest Rankings
  const handlePersonClickFromContest = (personName: string) => {
    // Speichere das ursprüngliche Contest, falls es ein Contest ist
    if (selectedElement && selectedElement.type === ExploreElementType.Contest) {
      setOriginalRanking(selectedElement);
    }
    
    // Erstelle ein Mock-Person-Element für das PersonModal
    const mockPerson = ExploreElementModel.create(
      {
        id: `mock_person_${personName.replace(/\s+/g, '_').toLowerCase()}`,
        name: personName,
        type: ExploreElementType.Person,
        startDate: new Date(),
        endDate: new Date(),
        favorite: false,
        country: '',
        gender: '',
        age: 0,
        location: '',
        nationality: '',
        description: `Contest participant: ${personName}`,
      },
      rootStore.environment
    );

    setSelectedElement(mockPerson);
    setIsPersonModalOpen(true);
  };

  // Person Click Handler für Ranking Rankings
  const handlePersonClickFromRanking = (personName: string) => {
    // Speichere das ursprüngliche Ranking
    if (selectedElement && selectedElement.type === ExploreElementType.Ranking) {
      setOriginalRanking(selectedElement);
    }
    
    // Erstelle ein Mock-Person-Element für das PersonModal
    const mockPerson = ExploreElementModel.create(
      {
        id: `mock_person_${personName.replace(/\s+/g, '_').toLowerCase()}`,
        name: personName,
        type: ExploreElementType.Person,
        favorite: false,
        startDate: new Date(),
        endDate: new Date(),
        country: '',
        gender: '',
        age: 0,
        location: '',
        nationality: '',
        description: `Ranking participant: ${personName}`,
      },
      rootStore.environment
    );

    setSelectedElement(mockPerson);
    setIsPersonModalOpen(true);
  };

  // Filter Modal Handlers
  const openFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const openPersonModal = (element: ExploreElement, score: number) => {
    setIsPersonModalOpen(false);
    setSelectedElement(null);
    setTimeout(() => {
      setSelectedElement(element);
      setSelectedScore(score);
      setIsPersonModalOpen(true);
    }, 0);
  };

  const closePersonModal = () => {
    setIsPersonModalOpen(false);
    setSelectedElement(null);
    if (originalRanking) {
      setSelectedElement(originalRanking);
      setOriginalRanking(null);
    }
  };

  const handleFilterCommit = async (filterData: FilterData) => {
    setAppliedFilters(filterData);
    if (filterData.searchQuery) {
      setSearchTerm(filterData.searchQuery);
    }
    closeFilterModal();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setAppliedFilters({
      locations: [],
      nationalities: [],
      elementTypes: [],
      favorites: false,
      genders: [],
      ageRange: undefined, // No age filter by default
      searchQuery: '',
    });
  };

  // State für die kombinierte Element-Liste
  const [elementList, setElementList] = useState<Array<ExploreElement>>([]);

  // Effect zum Laden und Kombinieren aller Elemente
  useEffect(() => {
    const loadAllElements = async () => {
      try {
        // Elemente aus dem Store
        const storeElements = exploreElementStore.elements.slice();

        // Mock-Daten als MobX-State-Tree Instanzen (nur wenn aktiviert)
        let mockElements: ExploreElement[] = [];
        if (debugStore.useMockData) {
          const mockDataRaw = getAllMockData();
          mockElements = mockDataRaw.map((item) =>
            ExploreElementModel.create(
              {
                id: item.id,
                name: item.name,
                type: item.type,
                startDate: new Date(),
                endDate: new Date(),
                favorite: item.favorite,
                country: item.country,
                gender: item.gender,
                age: item.age,
                location: item.location,
                nationality: item.nationality,
              },
              rootStore.environment
            )
          );
        }

        // ✅ KORRIGIERT: Ranker-User als separate MobX-Instanzen
        let rankerElements: ExploreElement[] = [];
        try {
          const rankerUsers = await loadRankerUsers();
          rankerElements = rankerUsers
            .map((userData) => {
              try {
                const element = ExploreElementModel.create(
                  userData,
                  rootStore.environment
                );
                return element;
              } catch (error) {
                return null;
              }
            })
            .filter(Boolean) as ExploreElement[];
        } catch (error) {
          logger.error(
            'Failed to load Ranker users for element list:',
            error
          );
        }

        // Kombiniere alle Elemente und entferne Duplikate basierend auf der ID
        const allElements = [
          ...storeElements,
          // ...mockElements,
          ...rankerElements,
        ];
        const uniqueElements = _.uniqBy(
          allElements,
          'id'
        ) as Array<ExploreElement>;
        setElementList(uniqueElements);
      } catch (error) {
        logger.error('❌ Error creating element list:', error);
        // Fallback: nur Store-Elemente
        setElementList(
          exploreElementStore.elements.slice() as Array<ExploreElement>
        );
      }
    };

    loadAllElements();
  }, [
    exploreElementStore.elements.length, // Use length to avoid accessing individual elements
    exploreElementStore.lastUpdated,
    forceUpdateCounter,
    debugStore.useMockData, // Add debugStore.useMockData to dependencies
    rootStore.environment,
    loadRankerUsers, // ✅ Dependency korrekt
  ]);

  // KORRIGIERTE Filter-Logik
  const filteredElementList: Array<ExploreElement> = useMemo(() => {
    const filtered = elementList.filter((element) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesName = element.name.toLowerCase().includes(searchLower);
        const matchesType = String(element.type)
          .toLowerCase()
          .includes(searchLower);
        const matchesLocation = (element as any).location
          ?.toLowerCase()
          .includes(searchLower);
        const matchesNationality = (element as any).nationality
          ?.toLowerCase()
          .includes(searchLower);

        if (
          !matchesName &&
          !matchesType &&
          !matchesLocation &&
          !matchesNationality
        ) {
          return false;
        }
      }

      // Locations filter
      if (appliedFilters.locations.length > 0) {
        const location = (element as any).location;
        if (!location || !appliedFilters.locations.includes(location)) {
          return false;
        }
      }

      // Nationalities filter
      if (appliedFilters.nationalities.length > 0) {
        const nationality = (element as any).nationality;
        if (
          !nationality ||
          !appliedFilters.nationalities.includes(nationality)
        ) {
          return false;
        }
      }

      // Element type filter
      if (appliedFilters.elementTypes.length > 0) {
        const elementTypeString = String(element.type);
        const filterTypeStrings = appliedFilters.elementTypes.map((type) =>
          String(type)
        );
        if (!filterTypeStrings.includes(elementTypeString)) {
          return false;
        }
      }

      // Favorites filter
      if (appliedFilters.favorites && !element.favorite) {
        return false;
      }

      // Gender filter
      if (appliedFilters.genders && appliedFilters.genders.length > 0) {
        const gender = (element as any).gender;
        if (!gender || !appliedFilters.genders.includes(gender as string)) {
          return false;
        }
      }

      // Age filter
      if (appliedFilters.ageRange && typeof (element as any).age === 'number') {
        const { min, max } = appliedFilters.ageRange;
        const age = (element as any).age;
        if (age < min || age > max) {
          return false;
        }
      }

      return true;
    });
    return filtered;
  }, [elementList, searchTerm, appliedFilters]);

  const favorites: Array<ExploreElement> = useMemo(() => {
    return filteredElementList.filter((item) => item.favorite);
  }, [filteredElementList]);

  const items: Array<ExploreElement> = (() => {
    let ret: Array<ExploreElement> = [];
    ret = _.uniqWith(filteredElementList, equalId);

    // Sort with user-created elements first, then by date
    return _.orderBy(
      ret,
      [
        (element) => !element.isOwnedByUser(rootStore.currentUser?.uid), // User-created elements first (false sorts before true)
        'date',
      ],
      ['asc', 'desc'] // User elements first (ascending), then newest date first (descending)
    );
  })();

  const _safeElements = useMemo(() => {
    // ✅ Alle detached nodes VORHER entfernen
    const aliveElements = exploreElementStore.elements.filter((element) => {
      try {
        return isAlive(element) && element.id; // Test property access
      } catch (error) {
        return false;
      }
    });

    // ✅ Dann lodash uniqWith auf sichere Elements anwenden
    return _.uniqWith(aliveElements, equalId);
  }, [exploreElementStore.elements]);

  // Search handler
  const handleSearch = (event: CustomEvent) => {
    setSearchTerm(event.detail.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  useIonViewDidEnter(() => {
    if (activeTab === 0 && items.length === 0) setActiveTab(1);
  });

  const events = useMemo(() => {
    const filteredEvents = items.filter(
      (item) => item.type === ExploreElementType.Event
    );

    // Sort with user-created elements first, then by date
    return _.orderBy(
      filteredEvents,
      [(element) => !element.isOwnedByUser(rootStore.currentUser?.uid), 'date'],
      ['asc', 'desc']
    );
  }, [items, rootStore.currentUser?.uid]);

  // State für echte Rankings vom freak_ranker Service
  const [rankerRankings, setRankerRankings] = useState<ExploreElement[]>([]);

  // Lade echte Rankings bei Component Mount oder Filter-Änderungen
  useEffect(() => {
    const loadRealRankings = async () => {
      try {
        // Warte auf Benutzer-Authentifizierung
        const currentUser = rootStore.currentUser;
        if (!currentUser) {
          return;
        }

        // Initialisiere RankerService falls nötig
        if (!rankerService.initialized) {
          await rankerService.ensureInitialized();
        }
        if (!rankerService.initialized) {
          return;
        }

        // Erstelle Custom Ranking mit angewendeten Filtern
        const filters = {
          locations: appliedFilters.locations,
          nationalities: appliedFilters.nationalities,
          genders: appliedFilters.genders,
          ageRange: appliedFilters.ageRange,
          searchQuery: appliedFilters.searchQuery || searchTerm,
        };

        // IMMER ein globales Ranking laden, es sei denn, Filter sind aktiv
        let rankingData = [];
        let rankingTitle = '';
        let rankingDescription = '';

        const hasActiveFilters =
          filters.locations.length > 0 ||
          filters.nationalities.length > 0 ||
          (filters.genders && filters.genders.length > 0) ||
          filters.searchQuery ||
          !!filters.ageRange; // Age filter is active if ageRange is defined

        if (hasActiveFilters) {
          rankingData = await rankerService.getCustomRanking(filters, 0, 50);

          // Erstelle dynamischen Titel basierend auf Filtern
          const filterParts = [];
          if (filters.locations.length > 0)
            filterParts.push(filters.locations.join(', '));
          if (filters.nationalities.length > 0)
            filterParts.push(filters.nationalities.join(', '));
          if (filters.genders && filters.genders.length > 0)
            filterParts.push(filters.genders.join(', '));
          if (filters.searchQuery) filterParts.push(`"${filters.searchQuery}"`);

          rankingTitle =
            filterParts.length > 0
              ? `Custom Ranking: ${filterParts.join(' • ')}`
              : 'Custom Ranking';
          rankingDescription = `Filtered ranking with ten users matching the criteria.`;
        } else {
          rankingData = await rankerService.getGlobalRanking(0, 50);
          rankingTitle = 'Global Ranking';
          rankingDescription = `Top ten users from the global leaderboard.`;
        }

        // ✅ FILTER RANKING DATA: Remove test/fake users from ranking
        const filteredRankingData = rankingData.filter((user) => {
          // Filter same criteria as in loadRankerUsers
          if (
            !user.displayName ||
            user.displayName.trim() === '' ||
            user.displayName === null ||
            user.score === null ||
            user.score === undefined
          ) {
            return false;
          }
          return true;
        });

        // Erstelle EIN EINZIGES Ranking-Element, das alle Benutzer enthält
        if (filteredRankingData.length > 0) {
          const rankingElement = ExploreElementModel.create(
            {
              id: hasActiveFilters
                ? 'ranking_live_custom'
                : 'ranking_live_global',
              name: rankingTitle,
              type: ExploreElementType.Ranking,
              favorite: false,
              country: 'Global',
              startDate: new Date(),
              endDate: new Date(),
              gender: 'Mixed',
              age: 0,
              location: 'Global',
              nationality: 'Global',
              description: rankingDescription,
              isPublic: true,
              isLive: true,
              // Speichere alle Ranking-Daten im rankings Array
              rankings: filteredRankingData.map((user, index) => ({
                userId: user.uid || `user_${index}`,
                userName: user.displayName || user.name || '',
                score: user.score || 0,
                position: index + 1,
                profileImageUrl: '',
                nationality: user.nationality || '',
              })),
            },
            rootStore.environment
          );

          setRankerRankings([rankingElement]);
        } else {
          setRankerRankings([]);
        }
      } catch (error) {
        logger.error('❌ Failed to load rankings from freak_ranker:', error);
        setRankerRankings([]);
      }
    };

    // Warte länger bevor Rankings geladen werden, um sicherzustellen dass User authentifiziert ist
    const timer = setTimeout(() => {
      if (rootStore.currentUser) {
        loadRealRankings();
      } else {
        setTimeout(() => {
          if (rootStore.currentUser) {
            loadRealRankings();
          }
        }, 2000);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [
    appliedFilters.locations,
    appliedFilters.nationalities,
    appliedFilters.genders,
    appliedFilters.ageRange,
    appliedFilters.searchQuery,
    searchTerm,
    rootStore.environment,
    rootStore.currentUser, // Abhängigkeit hinzugefügt
  ]);

  const rankings = useMemo(() => {
    const filteredRankings = items.filter(
      (item) => item.type === ExploreElementType.Ranking
    );

    // Mock-Rankings nur als Fallback wenn keine echten Daten verfügbar
    let mockRankingInstances: ExploreElement[] = [];
    if (debugStore.useMockData && rankerRankings.length === 0) {
      const mockRankingData = getAllMockData().filter(
        (item) => item.type === ExploreElementType.Ranking
      );

      // Erstelle MobX-State-Tree Instanzen für Mock-Rankings
      mockRankingInstances = mockRankingData.map((item) =>
        ExploreElementModel.create(
          {
            id: item.id,
            name: item.name,
            type: item.type,
            startDate: Date.now(),
            endDate: Date.now(),
            favorite: item.favorite,
            country: item.country,
            gender: item.gender,
            age: item.age,
            location: item.location,
            nationality: item.nationality,
          },
          rootStore.environment
        )
      );
    }

    // Kombiniere Store-Rankings, echte Rankings und Mock-Rankings
    const allRankings = [
      ...filteredRankings,
      ...rankerRankings,
      ...mockRankingInstances,
    ];

    // Entferne Duplikate basierend auf ID
    const uniqueRankings = _.uniqBy(allRankings, 'id');

    // Debug: Show which rankings have data
    uniqueRankings.forEach((ranking) => {
      const hasData = ranking.rankings && ranking.rankings.length > 0;
    });

    const finalFilteredRankings = uniqueRankings.filter((ranking) => {
      // Für Store-Rankings: Prüfe ob sie vom aktuellen Benutzer erstellt wurden
      const isUserCreated = ranking.isOwnedByUser?.(rootStore.currentUser?.uid);

      // Filter out rankings with no data (empty rankings array), BUT allow user-created rankings
      if (ranking.rankings && ranking.rankings.length === 0 && !isUserCreated) {
        return false;
      }

      // Always allow user-created rankings, even if they have no data yet
      if (isUserCreated) {
        return true;
      }

      // Search filter (nur für Store-Rankings, da Ranker-Rankings bereits gefiltert sind)
      if (searchTerm && !ranking.id.startsWith('ranking_live_')) {
        const searchLower = searchTerm.toLowerCase();
        const matchesName = ranking.name.toLowerCase().includes(searchLower);
        if (!matchesName) return false;
      }

      // Favorites filter
      if (appliedFilters.favorites && !ranking.favorite) {
        return false;
      }
      return true;
    });

    // Sort with user-created elements first, then by date
    return _.orderBy(
      finalFilteredRankings,
      [
        (element) => !element.isOwnedByUser(rootStore.currentUser?.uid), // User-created elements first
        'date',
      ],
      ['asc', 'desc'] // User elements first, then newest date first
    );
  }, [
    items,
    rankerRankings,
    debugStore.useMockData,
    searchTerm,
    appliedFilters.favorites,
    rootStore.environment,
    rootStore.currentUser?.uid,
  ]);

  const contests = useMemo(() => {
    const filteredContests = items.filter(
      (item) => item.type === ExploreElementType.Contest && !isMockElement(item)
    );

    // Kombiniere nur echte Store-Contests
    const allContests = [...filteredContests];

    // Entferne Duplikate basierend auf ID
    const uniqueContests = _.uniqBy(allContests, 'id');

    const finalFilteredContests = uniqueContests.filter((contest) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesName = contest.name.toLowerCase().includes(searchLower);
        if (!matchesName) return false;
      }

      // Favorites filter
      if (appliedFilters.favorites && !contest.favorite) {
        return false;
      }

      // Location filter
      if (appliedFilters.locations.length > 0) {
        const location = contest.location;
        if (!location || !appliedFilters.locations.includes(location)) {
          return false;
        }
      }

      return true;
    });

    // Sort with active contests first, then user-created elements, then by date
    return _.orderBy(
      finalFilteredContests,
      [
        // Active contests first
        (contest) => {
          const now = new Date();
          const startDate = contest.startDate;
          const endDate = contest.endDate;

          if (!startDate || !endDate) return 1;

          const start = new Date(startDate);
          const end = new Date(endDate);

          if (isNaN(start.getTime()) || isNaN(end.getTime())) return 1;

          const isActive = now >= start && now <= end;
          return isActive ? 0 : 1; // Active contests get 0 (first), others get 1
        },
        (element) => !element.isOwnedByUser(rootStore.currentUser?.uid), // User-created elements first
        'date',
      ],
      ['asc', 'asc', 'desc'] // Active first, user elements first, then newest date first
    );
  }, [
    items,
    searchTerm,
    appliedFilters.favorites,
    appliedFilters.locations,
    rootStore.currentUser?.uid,
  ]);

  const people = useMemo(() => {
    const filteredPeople = items.filter(
      (item) => item.type === ExploreElementType.Person
    );

    // Sort with user-created elements first, then by date
    return _.orderBy(
      filteredPeople,
      [
        (element) => !element.isOwnedByUser(rootStore.currentUser?.uid), // User-created elements first
        'date',
      ],
      ['asc', 'desc'] // User elements first, then newest date first
    );
  }, [items, rootStore.currentUser?.uid]);

  const agents = useMemo(() => {
    const filteredAgents = items.filter(
      (item) => item.type === ExploreElementType.Ai
    );

    // Sort with user-created elements first, then by date
    return _.orderBy(
      filteredAgents,
      [
        (element) => !element.isOwnedByUser(rootStore.currentUser?.uid), // User-created elements first
        'date',
      ],
      ['asc', 'desc'] // User elements first, then newest date first
    );
  }, [items, rootStore.currentUser?.uid]);

  // KORRIGIERTE hasActiveFilters Check
  const hasActiveFilters =
    appliedFilters.elementTypes.length > 0 ||
    appliedFilters.locations.length > 0 ||
    appliedFilters.nationalities.length > 0 ||
    appliedFilters.favorites ||
    (appliedFilters.genders && appliedFilters.genders.length > 0) ||
    !!appliedFilters.ageRange || // Age filter is active if ageRange is defined
    !!appliedFilters.searchQuery;

  // Handler-Funktionen für die CreateModal
  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  // Enhanced CRUD handlers with explicit store refresh
  const handleCreateElementWithRefresh = async (elementData: any) => {
    try {
      // Konvertiere String-Daten in die richtigen Typen für MobX-State-Tree
      const dateValue = elementData.date
        ? new Date(elementData.date)
        : new Date();

      // Validiere und korrigiere das Datum
      const validatedDate = isNaN(dateValue.getTime()) ? new Date() : dateValue;

      // Entferne id aus dem Payload, falls vorhanden
      const { id, ...elementDataWithoutId } = elementData;

      const elementToCreate = {
        ...elementDataWithoutId, // Use all data from CreateElementModal, aber ohne id
        date: validatedDate,
        country: elementData.country || '',
        gender: elementData.gender || '',
        age: elementData.age || 25,
        location: elementData.location || '',
        nationality: elementData.nationality || '',
        description: elementData.description || '',
        latitude: elementData.latitude || null,
        longitude: elementData.longitude || null,
        // Contest-specific fields will be included from elementData if present
      };

      await exploreElementStore.createElement(elementToCreate);

      // Debug-Logging für Element nach Erstellung
      const createdElement = exploreElementStore.getElementById(
        elementToCreate.id
      );

      // Element wird sofort angezeigt durch MobX-Reaktivität
      // Force UI refresh für sofortige Sichtbarkeit
      setForceUpdateCounter((prev) => prev + 1);

    } catch (error: any) {
      throw error;
    }
  };

  // Handler-Funktionen für Edit und Delete
  const handleEditElement = (element: ExploreElement) => {
    setElementToEdit(element);
    setIsEditModalOpen(true);
  };

  const handleUpdateElementWithRefresh = async (elementData: any) => {
    try {
      // Konvertiere String-Daten in die richtigen Typen für MobX-State-Tree
      const updatedData = {
        ...elementData,
        startDate: elementData.startDate
          ? new Date(elementData.startDate)
          : null,
        endDate: elementData.endDate ? new Date(elementData.endDate) : null,
        date: elementData.date ? new Date(elementData.date) : null,
      };

      await exploreElementStore.updateElement(updatedData);

      // Force UI refresh by reloading store data
      setTimeout(async () => {
        await exploreElementStore.loadElements();
        setForceUpdateCounter((prev) => prev + 1);
      }, 100);

      setIsEditModalOpen(false);
      setElementToEdit(null);
    } catch (error) {
      alert('Fehler beim Aktualisieren des Elements');
    }
  };

  const handleDeleteElementWithRefresh = async (element: ExploreElement) => {
    try {
      // Prüfe ob es ein Store-Element oder Mock-Element ist
      const isStoreElement = exploreElementStore.getElementById(element.id);

      if (isStoreElement) {
        // Lösche aus dem Store (und Firebase)
        await exploreElementStore.deleteElement(element.id);

        // Force UI refresh by reloading store data
        setTimeout(async () => {
          await exploreElementStore.loadElements();
          setForceUpdateCounter((prev) => prev + 1);
        }, 100);
      } else {
        await exploreElementStore.deleteElement(element.id);
      }

      // Schließe alle offenen Modals
      closeEventModal();
      closeRankingModal();
      closePersonModal();
    } catch (error) {
      alert('Fehler beim Löschen des Elements');
    }
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setElementToEdit(null);
  };

  // Agent Modal Handlers
  const openAgentModal = (agent: any) => {
    setSelectedAgent(agent);
    setIsAgentModalOpen(true);
  };
  const closeAgentModal = () => {
    setIsAgentModalOpen(false);
    setSelectedAgent(null);
  };

  useEffect(() => {
    // Simple trigger based on elements count and lastUpdated to avoid accessing individual elements
    setForceUpdateCounter((prev) => prev + 1);
  }, [exploreElementStore.elements.length, exploreElementStore.lastUpdated]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <IonPage>
        {/* All modals moved to IonPage level to prevent CSS inheritance issues */}
        <FilterEditModal
          isOpen={isFilterModalOpen}
          onDidDismiss={closeFilterModal}
          onCommit={handleFilterCommit}
          currentFilters={appliedFilters}
        />

        <CreateElementModal
          isOpen={isCreateModalOpen}
          onDidDismiss={closeCreateModal}
          onCreateElement={handleCreateElementWithRefresh}
        />

        <EditElementModal
          isOpen={isEditModalOpen}
          onDidDismiss={closeEditModal}
          onUpdateElement={handleUpdateElementWithRefresh}
          element={elementToEdit}
        />

        <EventModal
          isOpen={isEventModalOpen}
          onDidDismiss={closeEventModal}
          event={selectedElement}
          onEditElement={handleEditElement}
          onDeleteElement={handleDeleteElementWithRefresh}
        />

        <RankingModal
          isOpen={isRankingModalOpen}
          onDidDismiss={closeRankingModal}
          ranking={selectedElement}
          onEditElement={handleEditElement}
          onDeleteElement={handleDeleteElementWithRefresh}
          onPersonClick={handlePersonClickFromRanking}
        />

        <PersonModal
          key={selectedElement?.id || 'none'}
          isOpen={isPersonModalOpen}
          onDidDismiss={closePersonModal}
          person={selectedElement}
          score={selectedScore}
        />

        <ContestModal
          isOpen={isContestModalOpen}
          onDidDismiss={closeContestModal}
          contest={selectedElement}
          onEditElement={handleEditElement}
          onDeleteElement={handleDeleteElementWithRefresh}
          onPersonClick={handlePersonClickFromContest}
        />
        <AgentModal
          isOpen={isAgentModalOpen}
          onDidDismiss={closeAgentModal}
          agentName={selectedAgent?.name || 'AI Copilot'}
        />
        <IonHeader className="ion-no-border">
          <Toolbar
            openFilterModal={openFilterModal}
            searchTerm={searchTerm}
            onSearch={handleSearch}
            onClearSearch={clearSearch}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            onOpenCreateModal={openCreateModal}
          />
        </IonHeader>

        {/* Filter Pills und Results Counter - bei Suchbegriffen oder aktiven Filtern anzeigen */}
        {(searchTerm || hasActiveFilters) && (
          <div className="filter-section">
            {/* Filter Pills - nur bei aktiven Filtern anzeigen */}
            {hasActiveFilters && (
              <div className="filter-pills-container">
                {/* Location Filter Pills - Zeige nur den letzten ausgewählten Begriff */}
                {(() => {
                  // Finde den spezifischsten Location-Filter-Begriff
                  let displayLocation: string | null = null;

                  // Kontinente aus der locations.ts (in Großbuchstaben)
                  const continentNames = [
                    'EUROPE',
                    'NORTH_AMERICA',
                    'SOUTH_AMERICA',
                    'AFRICA',
                    'ASIA',
                    'OCEANIA',
                  ];

                  // Trenne Kontinente von anderen Locations
                  const nonContinentLocations = appliedFilters.locations.filter(
                    (location) =>
                      !continentNames.includes(location.toUpperCase())
                  );
                  const continentLocations = appliedFilters.locations.filter(
                    (location) =>
                      continentNames.includes(location.toUpperCase())
                  );

                  // Priorität: Skatepark > Stadt > Land > Kontinent
                  // Wenn es Nicht-Kontinent Locations gibt, zeige das letzte an
                  if (nonContinentLocations.length > 0) {
                    displayLocation =
                      nonContinentLocations[nonContinentLocations.length - 1];
                  } else if (continentLocations.length > 0) {
                    displayLocation =
                      continentLocations[continentLocations.length - 1];
                  }

                  return displayLocation ? (
                    <span key={displayLocation} className="filter-pill">
                      <Icon name="WorldIcon" size="small" />
                      {displayLocation}
                      <IonIcon
                        icon={close}
                        className="close-icon"
                        onClick={() => {
                          setAppliedFilters({
                            ...appliedFilters,
                            locations: [],
                          });
                        }}
                      />
                    </span>
                  ) : null;
                })()}

                {/* Nationality Filter Pills - Zeige nur den letzten ausgewählten Begriff */}
                {(() => {
                  // Finde den spezifischsten Nationality-Filter-Begriff
                  let displayNationality: string | null = null;

                  // Kontinente aus der locations.ts (in Großbuchstaben)
                  const continentNames = [
                    'EUROPE',
                    'NORTH_AMERICA',
                    'SOUTH_AMERICA',
                    'AFRICA',
                    'ASIA',
                    'OCEANIA',
                  ];

                  // Trenne Kontinente von Ländern
                  const countryNationalities =
                    appliedFilters.nationalities.filter(
                      (nationality) =>
                        !continentNames.includes(nationality.toUpperCase())
                    );
                  const continentNationalities =
                    appliedFilters.nationalities.filter((nationality) =>
                      continentNames.includes(nationality.toUpperCase())
                    );

                  // Priorität: Land > Kontinent
                  // Wenn es Länder gibt, zeige das letzte Land an
                  if (countryNationalities.length > 0) {
                    displayNationality =
                      countryNationalities[countryNationalities.length - 1];
                  } else if (continentNationalities.length > 0) {
                    displayNationality =
                      continentNationalities[continentNationalities.length - 1];
                  }

                  return displayNationality ? (
                    <span key={displayNationality} className="filter-pill">
                      <Icon name="NationalityIcon" size="small" />
                      {displayNationality}
                      <IonIcon
                        icon={close}
                        className="close-icon"
                        onClick={() => {
                          setAppliedFilters({
                            ...appliedFilters,
                            nationalities: [],
                          });
                        }}
                      />
                    </span>
                  ) : null;
                })()}

                {/* Gender Filter Pills - KORRIGIERTES Icon */}
                {appliedFilters.genders &&
                  appliedFilters.genders.map((gender) => (
                    <span key={gender} className="filter-pill">
                      <Icon name="AgeIcon" size="small" />
                      {gender}
                      <IonIcon
                        icon={close}
                        className="close-icon"
                        onClick={() => {
                          setAppliedFilters({
                            ...appliedFilters,
                            genders:
                              appliedFilters.genders?.filter(
                                (g) => g !== gender
                              ) || [],
                          });
                        }}
                      />
                    </span>
                  ))}

                {/* Age Filter Pill */}
                {appliedFilters.ageRange && (
                  <span className="filter-pill">
                    <Icon name="AgeIcon" size="small" />
                    {appliedFilters.ageRange.min}-{appliedFilters.ageRange.max}{' '}
                    Jahre
                    <IonIcon
                      icon={close}
                      className="close-icon"
                      onClick={() => {
                        setAppliedFilters({
                          ...appliedFilters,
                          ageRange: undefined,
                        });
                      }}
                    />
                  </span>
                )}

                {/* Favorites Filter Pill */}
                {appliedFilters.favorites && (
                  <span className="filter-pill">
                    <Icon name="HeartIcon" size="small" />
                    Favoriten
                    <IonIcon
                      icon={close}
                      className="close-icon"
                      onClick={() => {
                        setAppliedFilters({
                          ...appliedFilters,
                          favorites: false,
                        });
                      }}
                    />
                  </span>
                )}

                {/* Element Types Filter Pills */}
                {appliedFilters.elementTypes.map((type) => (
                  <span key={type} className="filter-pill">
                    {type}
                    <IonIcon
                      icon={close}
                      className="close-icon"
                      onClick={() => {
                        setAppliedFilters({
                          ...appliedFilters,
                          elementTypes: appliedFilters.elementTypes.filter(
                            (t) => t !== type
                          ),
                        });
                      }}
                    />
                  </span>
                ))}

                {/* Clear All Button */}
                <button
                  className="clear-all-pills-button"
                  onClick={clearFilters}
                >
                  Alle löschen
                </button>
              </div>
            )}

            {/* Results Counter */}
            <div className="results-counter">
              <span className="results-count">
                {filteredElementList.length}
              </span>
              von {elementList.length} Ergebnissen
            </div>
          </div>
        )}

        {/* No Results Message - zwischen Filter und Content */}
        {filteredElementList.length === 0 && (searchTerm || hasActiveFilters) && (
          <div className="no-results">
            <IonIcon
              src="/assets/icons/search.svg"
              className="no-results-icon"
            />
            <div className="no-results-title">Keine Ergebnisse gefunden</div>
            <div className="no-results-subtitle">
              Versuchen Sie andere Suchbegriffe oder Filter
            </div>
          </div>
        )}

        <IonContent className="ion-content-transparent" forceOverscroll={false}>
          <div className="explore-content">
            <FavoritesSection
              favorites={favorites}
              activeTab={activeTab}
              onOpenPersonModal={openPersonModal}
              onOpenEventModal={openEventModal}
              onOpenRankingModal={openRankingModal}
              onOpenContestModal={openContestModal}
            />

            <RankingsSection
              rankings={rankings}
              activeTab={activeTab}
              onOpenRankingModal={openRankingModal}
            />
            <EventSection
              events={events}
              activeTab={activeTab}
              openEventModal={openEventModal}
            />
            <ContestSection
              contests={contests}
              activeTab={activeTab}
              openContestModal={openContestModal}
            />
            <PeopleSection
              people={people}
              activeTab={activeTab}
              openPersonModal={openPersonModal}
            />
          </div>
        </IonContent>

        {/* Demo-Agent Button (nur zu Demo-Zwecken, z.B. unten rechts) 
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000 }}>
          <button
            style={{
              background: '#222',
              color: '#fff',
              border: 'none',
              borderRadius: 24,
              padding: '12px 24px',
              fontWeight: 600,
              fontSize: 18,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              cursor: 'pointer',
            }}
            onClick={() => openAgentModal(demoAgent)}
          >
            🤖 Spinnax Agent
          </button>
        </div> */}
      </IonPage>
    </Suspense>
  );
});

export default ExplorePage;
